import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReCAPTCHA from "react-google-recaptcha";

function Register() {
  const [step, setStep] = useState(1);
  const [profile, setProfileType] = useState("personal"); // default to personal
  const [formData, setFormData] = useState({});
  const [avatar, setAvatar] = useState(null);
  const [avatar1, setAvatar1] = useState(null);
  const [avatar2, setAvatar2] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatar1Preview, setAvatar1Preview] = useState(null);
  const [avatar2Preview, setAvatar2Preview] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showConfirmPassword1, setShowConfirmPassword1] = useState(false);
  const [emailCheckInProgress, setEmailCheckInProgress] = useState(false);
  const [emailExists, setEmailExists] = useState({
    email: false,
    email1: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const captchaRef = useRef(null);

  // Create refs for error fields
  const errorRefs = useRef({});

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Scroll to first error when fieldErrors changes
  useEffect(() => {
    if (Object.keys(fieldErrors).length > 0) {
      const firstErrorField = Object.keys(fieldErrors)[0];
      scrollToError(firstErrorField);
    }
  }, [fieldErrors]);

  const scrollToError = (fieldName) => {
    if (errorRefs.current[fieldName]) {
      setTimeout(() => {
        errorRefs.current[fieldName].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  };

  // Cleanup object URLs on component unmount
  useEffect(() => {
    return () => {
      [avatarPreview, avatar1Preview, avatar2Preview].forEach(preview => {
        if (preview) URL.revokeObjectURL(preview);
      });
    };
  }, [avatarPreview, avatar1Preview, avatar2Preview]);

  const checkEmailExists = async (email, isBusiness = false) => {
    try {
      setEmailCheckInProgress(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/php-api/check-email.php`,
        { email, profile: isBusiness ? 'professional' : 'personal' }
      );
      return res.data.exists;
    } catch (err) {
      console.error("Error checking email:", err);
      return false;
    } finally {
      setEmailCheckInProgress(false);
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePassword = (password) => {
    const re = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
    return re.test(password);
  };

  const validateUrl = (url) => {
    if (!url) return true;
     const re = /^(https?:\/\/|@)/i;
    return re.test(String(url).toLowerCase());
  };

  const validatePhone = (phone) => {
    const re = /^[0-9]{10,15}$/;
    return re.test(phone);
  };

  // Calculate password strength
  const getPasswordStrength = (password) => {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    
    // For number fields, only allow numbers
    if (e.target.type === 'number') {
      if (value && !/^\d+$/.test(value)) {
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleEmailBlur = async (e) => {
    const { name, value } = e.target;
    
    if ((name === 'email' || name === 'email1') && value) {
      const isBusinessEmail = name === 'email1';
      
      // First validate email format
      if (!validateEmail(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [name]: 'Please enter a valid email address'
        }));
        return;
      }
      
      // Then check if email exists
      const exists = await checkEmailExists(value, isBusinessEmail);
      setEmailExists(prev => ({ ...prev, [name]: exists }));
      
      if (exists) {
        setFieldErrors(prev => ({
          ...prev,
          [name]: 'This email is already registered'
        }));
      }
    }
  };

  const compressAndResizeImage = (file, width, height, maxSizeKB) => {
    return new Promise((resolve, reject) => {
      if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
        return reject(new Error("Only JPG, JPEG, PNG files are allowed."));
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          let quality = 0.9;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size / 1024 > maxSizeKB && quality > 0.1) {
                  quality -= 0.05;
                  tryCompress();
                } else {
                  resolve(new File([blob], file.name, { type: file.type }));
                }
              },
              file.type,
              quality
            );
          };
          tryCompress();
        };
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e, key) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    
    try {
      // First validate the image type
      if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
        throw new Error("Only JPG, JPEG, PNG files are allowed.");
      }

      // Use the same compression library as MyProfile for consistency
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 641,
        useWebWorker: true,
        maxIteration: 10,
      });

      // Then resize to exact dimensions
      const resizedFile = await resizeImageToExactDimensions(compressedFile, 600, 641);

      // Set the file and preview
      if (key === "avatar") {
        setAvatar(resizedFile);
        setAvatarPreview(URL.createObjectURL(resizedFile));
      } else if (key === "avatar1") {
        setAvatar1(resizedFile);
        setAvatar1Preview(URL.createObjectURL(resizedFile));
      } else if (key === "avatar2") {
        setAvatar2(resizedFile);
        setAvatar2Preview(URL.createObjectURL(resizedFile));
      }
    } catch (err) {
      setFieldErrors(prev => ({
        ...prev,
        [key]: err.message
      }));
      e.target.value = "";
    }
  };

  const labelMap = {
    // personal
    name: "Name",
    nickname: "Nickname",
    profession: "Profession",
    dob: "Date of Birth",
    marital: "Marital Status",
    children: "Children",
    religion: "Religion",
    eduqualification: "Educational Qualification",
    school: "School",
    college: "College",
    hobbies: "Hobbies",
    bloodgroup: "Blood Group",
    hometown: "Hometown",
    currentcity: "Current City",
    nationality: "Nationality",
    facebook: "Facebook",
    twitter: "Twitter",
    instagram: "Instagram",
    linked: "LinkedIn",
    phone: "Phone",
    email: "Email",
    password: "Password",
    awards: "Awards & Honour",
    message: "Interesting facts",
    // professional
    companyname: "Company Name",
    foundername: "Founder Name",
    catagory: "Industry",
    products: "Products",
    startedyear: "Started Year",
    boardmembers: "Board of Members",
    employee: "No. of Employees",
    gst: "GST Number",
    address: "Head Office",
    website: "Website",
    facebook1: "Facebook",
    twitter1: "Twitter",
    instagram1: "Instagram",
    linked1: "LinkedIn",
    phone1: "Phone",
    email1: "Email",
    password1: "Password",
    awards1: "Awards & Honour",
    message1: "Interesting facts about company",
  };

  const bloodGroups = [
    { value: "", label: "" },
    { value: "A+", label: "A+" },
    { value: "A-", label: "A-" },
    { value: "B+", label: "B+" },
    { value: "B-", label: "B-" },
    { value: "AB+", label: "AB+" },
    { value: "AB-", label: "AB-" },
    { value: "O+", label: "O+" },
    { value: "O-", label: "O-" },
  ];

  const validateStep = (s) => {
    const errors = {};
    let isValid = true;

    if (s === 1) {
      if (!profile) {
        errors.profile = "Please select a profile type.";
        isValid = false;
      }
      setFieldErrors(errors);
      return isValid;
    }

    if (s === 2) {
      if (profile === "personal") {
        const required = [
          "name",
          "profession",
          "marital",
          "eduqualification",
          "hobbies",
          "bloodgroup",
          "hometown",
          "currentcity",
          "nationality",
          "phone",
          "email",
          "password",
        ];
        
        required.forEach((k) => {
          if (!formData[k] || String(formData[k]).trim() === "") {
            errors[k] = `${labelMap[k]} is required`;
            isValid = false;
          }
        });

        // Email validation for personal profile
        if (!formData.email) {
          errors.email = "Email is required";
          isValid = false;
        } else if (!validateEmail(formData.email)) {
          errors.email = "Please enter a valid email address";
          isValid = false;
        } else if (emailExists.email) {
          errors.email = 'This email is already registered';
          isValid = false;
        }

        // Password validation
        if (formData.password && !validatePassword(formData.password)) {
          errors.password = "Password must be at least 8 characters with uppercase, lowercase, and a number";
          isValid = false;
        }

        // Password confirmation check
        if (formData.password && formData.confirmpassword && 
            formData.password !== formData.confirmpassword) {
          errors.confirmpassword = "Passwords do not match";
          isValid = false;
        }

        // Phone validation
        if (formData.phone && !validatePhone(formData.phone)) {
          errors.phone = "Please enter a valid phone number (10-15 digits)";
          isValid = false;
        }

        // URL validations for social media
        const urlFields = ["facebook", "twitter", "instagram", "linked"];
        urlFields.forEach(field => {
          if (formData[field] && !validateUrl(formData[field])) {
            errors[field] = "Please enter a valid URL or username starting with @";
            isValid = false;
          }
        });

      } else {
        const required = [
          "companyname",
          "foundername",
          "catagory",
          "startedyear",
          "boardmembers",
          "employee",
          "address",
          "phone1",
          "email1",
          "password1",
        ];
        
        required.forEach((k) => {
          if (!formData[k] || String(formData[k]).trim() === "") {
            errors[k] = `${labelMap[k]} is required`;
            isValid = false;
          }
        });

        // Email validation for professional profile
        if (!formData.email1) {
          errors.email1 = "Email is required";
          isValid = false;
        } else if (!validateEmail(formData.email1)) {
          errors.email1 = "Please enter a valid business email address";
          isValid = false;
        } else if (emailExists.email1) {
          errors.email1 = 'This email is already registered';
          isValid = false;
        }

        // Password validation
        if (formData.password1 && !validatePassword(formData.password1)) {
          errors.password1 = "Password must be at least 8 characters with uppercase, lowercase, and a number";
          isValid = false;
        }

        // Password confirmation check
        if (formData.password1 && formData.confirmpassword1 && 
            formData.password1 !== formData.confirmpassword1) {
          errors.confirmpassword1 = "Passwords do not match";
          isValid = false;
        }

        // Phone validation
        if (formData.phone1 && !validatePhone(formData.phone1)) {
          errors.phone1 = "Please enter a valid phone number (10-15 digits)";
          isValid = false;
        }

        // Website validation
        if (formData.website && !validateUrl(formData.website)) {
          errors.website = "Please enter a valid website URL";
          isValid = false;
        }

        // URL validations for social media
        const urlFields = ["facebook1", "twitter1", "instagram1", "linked1"];
        urlFields.forEach(field => {
          if (formData[field] && !validateUrl(formData[field])) {
            errors[field] = "Please enter a valid URL or username starting with @";
            isValid = false;
          }
        });

      }

      setFieldErrors(errors);
      return isValid;
    }

    if (s === 3) {
      // require message / message1
      if (profile === "personal") {
        if (!formData.message || formData.message.trim() === "") {
          errors.message = "Please add 'Interesting facts about you' (required)";
          isValid = false;
        }
      } else {
        if (!formData.message1 || formData.message1.trim() === "") {
          errors.message1 = "Please add 'Interesting facts about company' (required)";
          isValid = false;
        }
      }
      // Make profile photo mandatory
      if (!avatar) {
        errors.avatar = "Profile Photo is required";
        isValid = false;
      }
      
      setFieldErrors(errors);
      return isValid;
    }

    return true;
  };

  
  const handleNext = () => {
  if (!validateStep(step)) {
    // If validation fails, scroll to the first error
    const firstErrorField = Object.keys(fieldErrors)[0];
    if (firstErrorField) {
      scrollToError(firstErrorField);
    }
  } else {
    // Update step first
    setStep((s) => s + 1);
    
    // Then scroll to top after a small delay (ensures smooth scroll)
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 10); // Tiny delay to let React update the page
  }
};

  const handleBack = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate captcha
    if (!captchaValue) {
      setError("Please complete the CAPTCHA verification");
      return;
    }

    setIsSubmitting(true);

    // Final email check before submission
    if (profile === "personal" && formData.email) {
      const exists = await checkEmailExists(formData.email, false);
      if (exists) {
        setError('This email is already registered. Please use a different email.');
        setFieldErrors(prev => ({
          ...prev,
          email: 'This email is already registered'
        }));
        setStep(2);
        setIsSubmitting(false);
        // Scroll to top and then to error
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          scrollToError('email');
        }, 500);
        return;
      }
    } else if (profile === "professional" && formData.email1) {
      const exists = await checkEmailExists(formData.email1, true);
      if (exists) {
        setFieldErrors(prev => ({
          ...prev,
          email1: 'This email is already registered'
        }));
        setStep(2);
        setIsSubmitting(false);
        scrollToError('email1');
        return;
      }
    }

    // final validation: profile and required fields
    if (!profile) {
      setFieldErrors({ profile: "Profile type is required" });
      setStep(1);
      setIsSubmitting(false);
      scrollToError('profile');
      return;
    }

    // optional: re-check step 2 and 3
    if (!validateStep(2)) {
      setStep(2);
      setIsSubmitting(false);
      return;
    }
    
    if (!validateStep(3)) {
      setStep(3);
      setIsSubmitting(false);
      return;
    }

    // personal-only fields
    const personalOnly = [
      "name",
      "profession",
      "nickname",
      "dob",
      "marital",
      "children",
      "religion",
      "eduqualification",
      "school",
      "college",
      "hobbies",
      "bloodgroup",
      "hometown",
      "currentcity",
      "nationality",
      "facebook",
      "twitter",
      "instagram",
      "linked",
      "phone",
      "email",
      "password",
      "confirmpassword",
      "awards",
      "message",
    ];

    // professional-only fields
    const professionalOnly = [
      "companyname",
      "foundername",
      "catagory",
      "products",
      "startedyear",
      "boardmembers",
      "employee",
      "gst",
      "address",
      "website",
      "facebook1",
      "twitter1",
      "instagram1",
      "linked1",
      "phone1",
      "email1",
      "password1",
      "confirmpassword1",
      "awards1",
      "message1",
    ];

    const data = new FormData();

    // Append all form data
    for (let key in formData) {
      if (formData[key] !== undefined && formData[key] !== null) {
        data.append(key, formData[key]);
      }
    }

    // Append files with proper field names
    if (avatar) data.append("avatar", avatar, `avatar_${Date.now()}.jpg`);
    if (avatar1) data.append("avatar1", avatar1, `avatar1_${Date.now()}.jpg`);
    if (avatar2) data.append("avatar2", avatar2, `avatar2_${Date.now()}.jpg`);

    // Append formData keys but skip fields that belong to the other profile type
    for (let key in formData) {
      if (profile === "professional" && personalOnly.includes(key)) continue;
      if (profile === "personal" && professionalOnly.includes(key)) continue;
      // only append defined values (helps avoid undefined)
      if (formData[key] !== undefined) data.append(key, formData[key]);
    }

    data.append("profile", profile);
    data.append("captcha", captchaValue);

    // For backend compatibility: if business profile, set name from companyname
    if (profile === "professional" && formData.companyname) {
      data.append("name", formData.companyname);
    } else if (profile === "personal" && formData.name) {
      data.append("name", formData.name); // Ensure name is stored in db column
    }

    // Append images
    if (avatar) data.append("avatar", avatar);
    if (avatar1) data.append("avatar1", avatar1);
    // you intentionally store 3rd image into avatar2 column in backend earlier — kept as-is
    if (avatar2) data.append("avatar2", avatar2);

    data.append("payment_package", formData.payment_package || "");
    data.append("payment_status", formData.payment_status || "");
    data.append("payment_id", formData.payment_id || "");
    data.append("user_id", formData.user_id || "");
    data.append("username", formData.username || "");

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/php-api/register.php`,
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      if (res.data && res.data.success) {
        setError("");
        setSuccessMessage(res.data.message || "Registered successfully");
        setTimeout(() => {
          if (res.data.redirect) {
            window.location.href = res.data.redirect;
          }
        }, 2000);

        setFormData({});
        setAvatar(null);
        setAvatar1(null);
        setAvatar2(null);
        setAvatarPreview(null);
        setAvatar1Preview(null);
        setAvatar2Preview(null);
        setStep(1);
        setProfileType("personal");
        setFieldErrors({});
        captchaRef.current.reset();
        setCaptchaValue(null);
      } else {
        setError(res.data.message || "Failed to register");
        if (res.data.message && res.data.message.includes("email already registered")) {
          if (profile === "personal") {
            setFieldErrors(prev => ({ ...prev, email: "This email is already registered" }));
            setStep(2);
          } else {
            setFieldErrors(prev => ({ ...prev, email1: "This email is already registered" }));
            setStep(2);
          }
        }
      }
    } catch (err) {
      console.error(err);
      setError("Error occurred during registration");
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
        if (err.response.data.message.includes("email already registered")) {
          if (profile === "personal") {
            setFieldErrors(prev => ({ ...prev, email: "This email is already registered" }));
            setStep(2);
          } else {
            setFieldErrors(prev => ({ ...prev, email1: "This email is already registered" }));
            setStep(2);
          }
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resizeImageToExactDimensions = (file, width, height) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const newFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(newFile);
            } else {
              reject(new Error('Canvas is empty'));
            }
          },
          file.type,
          0.9
        );
        URL.revokeObjectURL(url);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Image load error'));
      };

      img.src = url;
    });
  };

  const floatingInput = (name, label, required = false, type = "text") => {
    const isPassword = type === "password";
    const isUrlField = ["facebook", "twitter", "instagram", "linked", "website"].includes(name);
    const isNumberField = type === "number";
    const isPhoneField = name === "phone" || name === "phone1";
    const isEmailField = name === "email" || name === "email1";
    
    return (
      <div className="relative mb-4" ref={el => errorRefs.current[name] = el}>
        {type === "select" ? (
          <div className="relative">
            <select
              id={name}
              name={name}
              value={formData[name] || ""}
              required={required}
              onChange={handleChange}
              onBlur={(e) => {
                // Special handling for email fields
                if ((name === 'email' || name === 'email1') && e.target.value) {
                  const isBusinessEmail = name === 'email1';
                  checkEmailExists(e.target.value, isBusinessEmail).then(exists => {
                    if (exists) {
                      setFieldErrors(prev => ({
                        ...prev,
                        [name]: 'This email is already registered'
                      }));
                    }
                  });
                }
                handleChange(e); // Call the original handleChange
              }}
              className={`peer h-10 w-full border-b-2 border-gray-300 text-gray-900 bg-transparent focus:outline-none ${
                fieldErrors[name] ? "border-red-500" : "focus:border-green-600"
              }`}
            >
              {bloodGroups.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label
              htmlFor={name}
              className={`absolute left-0 transition-all ${
                formData[name]
                  ? "-top-3.5 text-sm text-green-600"
                  : "top-2 text-base text-gray-400"
              } peer-focus:-top-3.5 peer-focus:text-sm peer-focus:text-green-600`}
            >
              {label} {required && <span className="text-red-600">*</span>}
            </label>
          </div>
        ) : (
          <div className="relative">
            <input
              id={name}
              type={
                isPassword 
                  ? (name.includes('1') 
                      ? (showPassword1 ? "text" : "password")
                      : (showPassword ? "text" : "password"))
                  : type
              }
              name={name}
              value={formData[name] || ""}
              required={required}
              onChange={handleChange}
              onKeyDown={isNumberField ? (e) => {
                // Allow: backspace, delete, tab, escape, enter, numbers
                if (
                  [46, 8, 9, 27, 13, 110].includes(e.keyCode) || 
                  (e.keyCode === 65 && (e.ctrlKey || e.metaKey)) || // Ctrl+A
                  (e.keyCode >= 35 && e.keyCode <= 40) || // Home, End, Left, Right
                  (e.keyCode >= 48 && e.keyCode <= 57 && !e.shiftKey) || // 0-9
                  (e.keyCode >= 96 && e.keyCode <= 105) // Numpad 0-9
                ) {
                  return;
                }
                e.preventDefault();
              } : undefined}
              className={`peer h-10 w-full border-b-2 border-gray-300 text-gray-900 placeholder-transparent focus:outline-none ${
                fieldErrors[name] 
                  ? "border-red-500" 
                  : (isUrlField && formData[name] && !validateUrl(formData[name]))
                    ? "border-yellow-500"
                    : "focus:border-green-600"
              }`}
              placeholder={label}
            />
            <label
              htmlFor={name}
              className="absolute left-0 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-2 peer-focus:-top-3.5 peer-focus:text-sm peer-focus:text-green-600"
            >
              {label} {required && <span className="text-red-600">*</span>}
            </label>
            
            {isPassword && (
              <button
                type="button"
                className="absolute right-0 top-2 text-gray-500 hover:text-gray-700"
                onClick={() => {
                  if (name.includes('1')) {
                    name.includes('confirm') ? setShowConfirmPassword1(!showConfirmPassword1) : setShowPassword1(!showPassword1);
                  } else {
                    name.includes('confirm') ? setShowConfirmPassword(!showConfirmPassword) : setShowPassword(!showPassword);
                  }
                }}
              >
                {name.includes('1') 
                  ? (name.includes('confirm') 
                      ? (showConfirmPassword1 ? "Hide" : "Show") 
                      : (showPassword1 ? "Hide" : "Show"))
                  : (name.includes('confirm') 
                      ? (showConfirmPassword ? "Hide" : "Show") 
                      : (showPassword ? "Hide" : "Show"))}
              </button>
            )}
            
            {isPassword && formData[name] && (
              <div className="mt-2">
                <div className="flex items-center">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className={`h-2.5 rounded-full ${
                        getPasswordStrength(formData[name]) <= 2 ? "bg-red-500" :
                        getPasswordStrength(formData[name]) === 3 ? "bg-yellow-500" : "bg-green-500"
                      }`} 
                      style={{ width: `${(getPasswordStrength(formData[name]) / 5) * 100}%` }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-gray-500">
                    {getPasswordStrength(formData[name]) <= 2 ? "Weak" :
                     getPasswordStrength(formData[name]) === 3 ? "Medium" : "Strong"}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        
        {fieldErrors[name] && (
          <p className="text-red-500 text-xs mt-1">{fieldErrors[name]}</p>
        )}
        
        {isUrlField && formData[name] && !validateUrl(formData[name]) && !fieldErrors[name] && (
          <p className="text-yellow-600 text-xs mt-1"> Please enter a valid URL or username starting with @</p>
        )}
        
        {isPhoneField && formData[name] && !validatePhone(formData[name]) && !fieldErrors[name] && (
          <p className="text-yellow-600 text-xs mt-1">Please enter a valid phone number</p>
        )}
         {isEmailField && emailCheckInProgress && (
          <p className="text-gray-500 text-xs mt-1">Checking email...</p>
        )}
      </div>
    );
  };

  const floatingTextarea = (name, label, required = false) => (
    <div className="relative mt-4 mb-4" ref={el => errorRefs.current[name] = el}>
      <textarea
        id={name}
        name={name}
        value={formData[name] || ""}
        required={required}
        onChange={handleChange}
        rows="4"
        placeholder={label}
        className={`peer w-full border-b-2 border-gray-300 text-gray-900 placeholder-transparent focus:outline-none ${
          fieldErrors[name] ? "border-red-500" : "focus:border-green-600"
        }`}
      />
      <label
        htmlFor={name}
        className="absolute left-0 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-2 peer-focus:-top-3.5 peer-focus:text-sm peer-focus:text-green-600"
      >
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      {fieldErrors[name] && (
        <p className="text-red-500 text-xs mt-1">{fieldErrors[name]}</p>
      )}
    </div>
  );

  const maritalOptions = [
    { value: "", label: "" },
    { value: "Single", label: "Single" },
    { value: "Married", label: "Married" },
    { value: "Divorced", label: "Divorced" },
    { value: "Widowed", label: "Widowed" },
  ];

  const renderStep = () => {
    if (step === 1) {
      return (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-center">Step 1 — Profile Type</h2>
          {fieldErrors.profile && (
            <p className="text-red-500 text-sm text-center mb-2">{fieldErrors.profile}</p>
          )}
          <div className="flex justify-center mb-8 space-x-6">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="profile"
                value="personal"
                checked={profile === "personal"}
                onChange={() => {
                  setProfileType("personal");
                  if (fieldErrors.profile) {
                    setFieldErrors(prev => {
                      const newErrors = {...prev};
                      delete newErrors.profile;
                      return newErrors;
                    });
                  }
                }}
                className="accent-green-600"
              />
              <span className="font-medium">Individual Biography</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="profile"
                value="professional"
                checked={profile === "professional"}
                onChange={() => {
                  setProfileType("professional");
                  if (fieldErrors.profile) {
                    setFieldErrors(prev => {
                      const newErrors = {...prev};
                      delete newErrors.profile;
                      return newErrors;
                    });
                  }
                }}
                className="accent-green-600"
              />
              <span className="font-medium">Business Profile</span>
            </label>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-center">Step 2 — Basic Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {profile === "personal" ? (
              <>
                {floatingInput("name", "Name", true)}
                {floatingInput("nickname", "Nickname")}
                {floatingInput("profession", "Profession", true)}
                {floatingInput("dob", "Date of Birth", false, "date")}
                {/* Marital Status dropdown */}
                <div className="relative mb-4" ref={el => errorRefs.current.marital = el}>
                  <select
                    id="marital"
                    name="marital"
                    value={formData.marital || ""}
                    required
                    onChange={handleChange}
                    className={`peer h-10 w-full border-b-2 border-gray-300 text-gray-900 bg-transparent focus:outline-none ${
                      fieldErrors.marital ? "border-red-500" : "focus:border-green-600"
                    }`}
                    style={{ color: formData.marital ? "#111" : "#a3a3a3" }}
                  >
                    {maritalOptions.map((opt) => (
                      <option key={opt.value} value={opt.value} disabled={opt.value === ""}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <label
                    htmlFor="marital"
                    className={`absolute left-0 transition-all ${
                      formData.marital
                        ? "-top-3.5 text-sm text-green-600"
                        : "top-2 text-base text-gray-400"
                    } peer-focus:-top-3.5 peer-focus:text-sm peer-focus:text-green-600`}
                  >
                    Marital Status <span className="text-red-600">*</span>
                  </label>
                  {fieldErrors.marital && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.marital}</p>
                  )}
                </div>
                {/* Only show Children field if marital status is NOT "Single" */}
                {formData.marital && formData.marital !== "Single" && floatingInput("children", "Children")}
                {floatingInput("religion", "Religion")}
                {floatingInput("eduqualification", "Educational Qualification", true)}
                {floatingInput("school", "School")}
                {floatingInput("college", "College")}
                {floatingInput("hobbies", "Hobbies", true)}
                
                {/* Blood Group dropdown */}
                <div className="relative mb-4" ref={el => errorRefs.current.bloodgroup = el}>
                  <select
                    id="bloodgroup"
                    name="bloodgroup"
                    value={formData.bloodgroup || ""}
                    required
                    onChange={handleChange}
                    className={`peer h-10 w-full border-b-2 border-gray-300 text-gray-900 bg-transparent focus:outline-none ${
                      fieldErrors.bloodgroup ? "border-red-500" : "focus:border-green-600"
                    }`}
                    style={{ color: formData.bloodgroup ? "#111" : "#a3a3a3" }}
                  >
                    {bloodGroups.map((opt) => (
                      <option key={opt.value} value={opt.value} disabled={opt.value === ""}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <label
                    htmlFor="bloodgroup"
                    className={`absolute left-0 transition-all ${
                      formData.bloodgroup
                        ? "-top-3.5 text-sm text-green-600"
                        : "top-2 text-base text-gray-400"
                    } peer-focus:-top-3.5 peer-focus:text-sm peer-focus:text-green-600`}
                  >
                    Blood Group <span className="text-red-600">*</span>
                  </label>
                  {fieldErrors.bloodgroup && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.bloodgroup}</p>
                  )}
                </div>
                
                {floatingInput("hometown", "Hometown", true)}
                {floatingInput("currentcity", "Current City", true)}
                {floatingInput("nationality", "Nationality", true)}
                {floatingInput("facebook", "Facebook", false, "url")}
                {floatingInput("twitter", "X", false, "url")}
                {floatingInput("instagram", "Instagram", false, "url")}
                {floatingInput("linked", "LinkedIn", false, "url")}
                {floatingInput("phone", "Phone", true, "tel")}
                {floatingInput("email", "Email", true, "email")}
                {floatingInput("password", "Password", true, "password")}
                {floatingInput("confirmpassword", "Confirm Password", true, "password")}
              </>
            ) : (
              <>
                {floatingInput("companyname", "Company Name", true)}
                {floatingInput("foundername", "Founder Name", true)}
                {floatingInput("catagory", "Industry", true)}
                {floatingInput("products", "Products")}
                {floatingInput("startedyear", "Started Year", true, "date")}
                {floatingInput("boardmembers", "Board of Members", true, "number")}
                {floatingInput("employee", "No. of Employees", true, "number")}
                {floatingInput("gst", "GST Number")}
                {floatingInput("address", "Head Office", true)}
                {floatingInput("website", "Website", false, "url")}
                  {floatingInput("facebook1", "Facebook", false, "url")}
                {floatingInput("twitter1", "X", false, "url")}
                {floatingInput("instagram1", "Instagram", false, "url")}
                {floatingInput("linked1", "LinkedIn", false, "url")}
                {floatingInput("phone1", "Phone", true, "tel")}
                {floatingInput("email1", "Email", true, "email")}
                {floatingInput("password1", "Password", true, "password")}
                {floatingInput("confirmpassword1", "Confirm Password", true, "password")}
              </>
            )}
          </div>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-center">Step 3 — Extra Info & Upload</h2>

          {profile === "personal"
            ? floatingTextarea("awards", "List of Awards & Honour")
            : floatingTextarea("awards1", "List of Awards & Honour")}

          {profile === "personal"
            ? floatingTextarea("message", "Interesting facts about you", true)
            : floatingTextarea("message1", "Interesting facts about company", true)}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Profile Photo <span className="text-red-600">*</span></label>
              <input
                type="file"
                accept="image/png, image/jpeg"
                onChange={(e) => handleFileChange(e, "avatar")}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-semibold
                  file:bg-green-50 file:text-green-700
                  hover:file:bg-green-100"
              />
              {fieldErrors.avatar && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.avatar}</p>
              )}
              {avatarPreview && (
                <img src={avatarPreview} alt="avatar" className="mt-2 w-32 h-32 object-cover rounded" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Additional Photo</label>
              <input
                type="file"
                accept="image/png, image/jpeg"
                onChange={(e) => handleFileChange(e, "avatar1")}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-semibold
                  file:bg-green-50 file:text-green-700
                  hover:file:bg-green-100"
              />
              {fieldErrors.avatar1 && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.avatar1}</p>
              )}
              {avatar1Preview && (
                <img src={avatar1Preview} alt="avatar1" className="mt-2 w-32 h-32 object-cover rounded" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Additional Photo 2</label>
              <input
                type="file"
                accept="image/png, image/jpeg"
                onChange={(e) => handleFileChange(e, "avatar2")}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-semibold
                  file:bg-green-50 file:text-green-700
                  hover:file:bg-green-100"
              />
              {fieldErrors.avatar2 && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.avatar2}</p>
              )}
              {avatar2Preview && (
                <img src={avatar2Preview} alt="avatar2" className="mt-2 w-32 h-32 object-cover rounded" />
              )}
            </div>
          </div>
          <br></br>
          <div className="text-sm"><b>Note:</b> For a better image view, the size should be 600px in width and 641px in height.</div>
        </div>
      );
    }

    if (step === 4) {
      // Review step
      return (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-center">Step 4 — Review & Submit</h2>

          <div className="bg-gray-50 p-4 rounded mb-4">
            <div className="mb-2">
              <strong>Profile Type:</strong> {profile === "personal" ? "Individual" : "Business"}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.keys(formData).length === 0 && <div className="col-span-2 text-gray-500">No details entered yet.</div>}

              {Object.entries(formData).map(([k, v]) => (
                <div key={k} className="p-2 border rounded bg-white">
                  <div className="text-sm text-gray-600">{labelMap[k] || k}</div>
                  <div className="font-medium">{String(v)}</div>
                </div>
              ))}

                {/* Add CAPTCHA here */}
   
  <div className="flex justify-center my-6 relative">
        <ReCAPTCHA
          ref={captchaRef}
          sitekey="6LeQgv4UAAAAADXZOtgpI5_EIC7Av7CUjB5K8zdP" // Replace with your actual site key
          onChange={(value) => setCaptchaValue(value)}
        />
    
      
      {/* Show error if CAPTCHA not completed */}
      {error && error.includes("CAPTCHA") && (
        <div className="text-red-500 text-center mb-4 text-xs absolute top-20">{error}</div>
      )}
        </div>

              {/* <div className="p-2">
                <div className="text-sm text-gray-600">Profile Photo</div>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="avatar" className="w-28 h-28 object-cover rounded" />
                ) : (
                  <div className="text-sm text-gray-400">Not uploaded</div>
                )}
              </div>
              <div className="p-2">
                <div className="text-sm text-gray-600">Additional Photo</div>
                {avatar1Preview ? (
                  <img src={avatar1Preview} alt="avatar1" className="w-28 h-28 object-cover rounded" />
                ) : (
                  <div className="text-sm text-gray-400">Not uploaded</div>
                )}
              </div>
              <div className="p-2">
                <div className="text-sm text-gray-600">Additional Photo 2</div>
                {avatar2Preview ? (
                  <img src={avatar2Preview} alt="avatar2" className="w-28 h-28 object-cover rounded" />
                ) : (
                  <div className="text-sm text-gray-400">Not uploaded</div>
                )}
              </div> */}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Header />
      <div className="page-body-container">
        <div className="max-w-4xl mx-auto my-10 bg-white p-6 rounded shadow-lg">
          <h1 className="text-2xl font-bold mb-4 text-center">Register Your Profile</h1>
          {successMessage && (
            <div className="bg-green-100 text-green-800 p-2 rounded mb-4 text-center">
              {successMessage}
            </div>
          )}
          {error && (
            <div className="bg-red-100 text-red-800 p-2 rounded mb-4 text-center">
              {error}
            </div>
          )}

          {/* progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <div>Step {step} of 4</div>
              <div className="w-2/3 text-right">{profile === "personal" ? "Individual" : "Business"}</div>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 rounded-full bg-primarycolor"
                style={{ width: `${(step / 4) * 100}%` }}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {renderStep()}

            <div className="flex justify-between mt-6">
              <div>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
                  >
                    Back
                  </button>
                )}
              </div>

              <div>
                {step < 4 && (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="primarybutton"
                  >
                    Next
                  </button>
                )}

                {step === 4 && (
                  <button
                    type="submit"
                    className="primarybutton"
                  >
                    Submit Registration
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
        <Footer />
      </div>
    </>
  );
}
export default Register;